import React, { useState, useEffect } from 'react';
import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import api from '../../utils/api';
import PackageInquiryModal from '../../components/modals/PackageInquiryModal';

const dummyData = [
  { 
    name: 'Standard Developer', 
    color: '#b8860b', 
    icon: 'fa-building', 
    price: '19,999', 
    period: 'Yearly',
    tagline: 'Perfect for established developers',
    categories: [
      {
        title: 'Project Listings',
        features: ['5 Active Projects', 'Unlimited Phases', 'Project Gallery enabled', 'Construction Updates']
      },
      {
        title: 'Media & Branding',
        features: ['20 Images per Project', 'Walkthrough Video support', 'Brochure Downloads', 'Developer Logo Branding']
      },
      {
        title: 'Lead Management',
        features: ['Direct Lead Dashboard', 'WhatsApp Integration', 'Basic Analytics', 'Email Notifications']
      }
    ]
  },
  { 
    name: 'Premium Developer', 
    color: '#8b4513', 
    icon: 'fa-crown', 
    price: '49,999', 
    period: 'Yearly',
    tagline: 'Maximum visibility for top brands',
    popular: true,
    categories: [
      {
        title: 'Project Listings',
        features: ['15 Active Projects', 'Featured Project Tag', 'Priority Search Ranking', 'Virtual Tour Support']
      },
      {
        title: 'Media & Branding',
        features: ['Unlimited Images', 'Multiple Video Links', 'Custom Micro-site', 'Corporate Video Support']
      },
      {
        title: 'Advanced Leads',
        features: ['Advanced CRM access', 'Team Collaboration', 'Detailed Analytics', 'Real-time Lead Alerts']
      }
    ]
  }
];

export default function DeveloperPackages() {
  const [packages, setPackages] = useState(dummyData);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<{ name: string; type: string } | null>(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await api.getPackages('developer');
        if (data && data.length > 0) {
          setPackages(data);
        }
      } catch (err) {
        console.error('Failed to fetch developer packages, using dummy data.');
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const cleanTitle = (title: string) => {
    // Remove emojis
    let cleaned = title.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
    
    const mapping: Record<string, string> = {
      'LISTINGS SYSTEM': 'CORE',
      'MEDIA SYSTEM': 'DISPLAY',
      'LOCATION + SEARCH': 'LOCALITY',
      'VISIBILITY SYSTEM': 'BOOST',
      'LEAD MGMT': 'ENGAGE',
      'ADVANCED FEATURES': 'PREMIUM'
    };

    for (const [key, value] of Object.entries(mapping)) {
      if (cleaned.toUpperCase().includes(key)) return value;
    }

    // Fallback: strip numbers and parentheses
    return cleaned.replace(/^\d+\.\s*/, '').replace(/\s*\(.*\)$/, '').trim().toUpperCase();
  };

  const cleanFeature = (text: string) => {
    return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
  };

  return (
    <div className="bg-[#F4F6F9] min-h-screen font-sans overflow-x-hidden">
      <Navbar transparent={false} />

      <PackageInquiryModal 
        isOpen={!!selectedPackage}
        onClose={() => setSelectedPackage(null)}
        packageName={selectedPackage?.name || ''}
        packageType="Developer"
      />

      {/* Hero Section */}
      <section className="relative pt-48 pb-32 overflow-hidden bg-gradient-to-br from-[#05144b] to-[#4c1d95]">
        {/* Subtle animated background shapes */}
        <div className="absolute inset-0 z-0 overflow-hidden">
           <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] bg-[#D4A017] opacity-[0.05] rounded-full blur-[120px]"></div>
           <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] bg-[#D4A017] opacity-[0.05] rounded-full blur-[120px]"></div>
        </div>
        
        <div className="container relative z-10 mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/5 border border-white/10 text-[#D4A017] text-[10px] font-bold mb-8 shadow-xl">
            <i className="fa-solid fa-gem opacity-70"></i>
            Developer Partnership Program
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight leading-tight">
            Elevate Your <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4A017] to-[#f3cf7a]">
              Brand Presence
            </span>
          </h1>
          <p className="max-w-2xl mx-auto text-gray-400 text-base md:text-lg font-medium leading-relaxed opacity-70">
            Showcase your architectural masterpieces to a curated audience of premium buyers. 
            Choose a plan that matches your project's scale and ambition.
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="relative z-20 -mt-10 pb-32">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
             <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b8860b]"></div>
             </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6">
              {packages.map((pkg, idx) => (
                <div 
                  key={pkg.name} 
                  className={`group relative flex flex-col bg-white rounded-[2.5rem] shadow-xl border border-gold/10 hover:border-gold/40 transition-all duration-500 hover:-translate-y-2 overflow-hidden ${pkg.popular ? 'scale-[1.02] z-10 border-gold/50' : ''}`}
                >
                  {pkg.popular && (
                    <div className="absolute top-6 -right-12 bg-[#D4A017] text-white text-[8px] font-black px-14 py-2 rotate-45 shadow-lg z-10">
                      Popular
                    </div>
                  )}

                  <div className="p-8 border-b border-gray-100 relative overflow-hidden bg-gradient-to-br from-[#05144b] to-[#4c1d95] text-white">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg shadow-lg mb-6" style={{ backgroundColor: `${pkg.color}20`, color: pkg.color }}>
                      <i className={`fa-solid ${pkg.icon}`}></i>
                    </div>
                    
                    <h3 className="text-2xl font-bold tracking-tight mb-1 text-[#D4A017] truncate">{pkg.name}</h3>
                    <p className="text-[11px] font-bold text-gray-400 mb-6 line-clamp-1">{pkg.tagline}</p>
                    
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold tracking-tight text-white">₹{pkg.price}</span>
                      <span className="text-[11px] font-bold text-gray-500">/ {pkg.period}</span>
                    </div>
                  </div>

                  <div className="p-8 flex-grow bg-gray-50/30">
                    {pkg.categories.map((cat, ci) => (
                      <div key={ci} className="mb-6 last:mb-0">
                        <h4 className="text-[11px] font-black text-black mb-3 border-b border-gray-200 pb-1">{cleanTitle(cat.title)}</h4>
                        <ul className="space-y-3">
                          {cat.features.map((feat, fi) => {
                             const isAvailable = !feat.includes('❌');
                             const cleanText = cleanFeature(feat);
                             return (
                               <li key={fi} className={`flex items-start gap-2 ${isAvailable ? '' : 'opacity-30'}`}>
                                 <i className={`fa-solid ${isAvailable ? 'fa-circle-check text-[#D4A017]' : 'fa-circle-xmark text-gray-400'} text-xs mt-1`}></i>
                                 <span className="text-[13px] font-bold text-black leading-tight">{cleanText}</span>
                               </li>
                             );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <div className="p-6 bg-white">
                    <button 
                      onClick={() => setSelectedPackage({ name: pkg.name, type: 'developer' })}
                      className={`w-full py-4 rounded-xl text-sm font-bold transition-all shadow-lg active:scale-95 ${pkg.popular ? 'bg-[#D4A017] text-white hover:bg-[#8b4513]' : 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white hover:opacity-90'}`}
                    >
                      Choose Plan
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <FooterTop bg="bg-white" />
      <Footer />
    </div>
  );
}